import { BasePresenter } from "core/presenter";

import {
  ProductRepository,
  UserRepository,
  NewSaleReportRepository
} from "core/repository";

import Moment from "moment";
import * as FileSaver from "file-saver";

import { SaleStatus, DocumentTypes } from "core/enum";

import { fn } from "util";

export default class ProductSalesReportPresenter extends BasePresenter {
  initialState() {
    const urlParams = new URLSearchParams(window.location.hash);
    const startDate = Moment(urlParams.get("inicio"), "DD-MM-YYYY");
    const endDate = Moment(urlParams.get("fim"), "DD-MM-YYYY");
    return {
      loading: true,
      filters: {
        dateFrom: startDate.isValid()
          ? startDate
          : Moment()
              .startOf("day")
              .subtract(7, "days"),
        dateTo: endDate.isValid() ? endDate : Moment().endOf("day")
      },
      pagination: {
        page: 1,
        perPage: 25,
        total: 0
      },
      summary: {
        revenues: 0,
        costs: 0,
        profit: 0
      },
      results: [],
      order: {
        field: "product_name",
        direction: "ASC"
      },
      productSearchOptions: {
        online: true,
        repository: ProductRepository,
        order: [{ field: "name", direction: "ASC" }],
        pagination: { page: 1, perPage: 25 },
        filter: filter => this.getProductFilters(filter)
      },
      userSearchOptions: {
        online: true,
        repository: UserRepository,
        order: [{ field: "name", direction: "ASC" }],
        pagination: { page: 1, perPage: 25 },
        filter: async filter => await this.getUserFilters(filter)
      },
      product: {
        name: "",
        uuid: null
      },
      user: {
        name: "",
        uuid: null
      },
      mode: "product"
    };
  }

  async onStart() {
    this.load();
  }

  async load() {
    this.viewModel.loading = true;
    this.viewModel.results = [];

    let resultsPromise;

    if (this.viewModel.product.uuid) {
      this.viewModel.mode = "sale";
      resultsPromise = this.loadSaleResults();
    } else {
      this.viewModel.mode = "product";
      resultsPromise = this.loadProductResults();
    }

    await Promise.all([resultsPromise, this.loadSummary()]);

    this.viewModel.loading = false;
  }

  async loadProductResults() {
    this.renewCancelToken();

    const filters = await this.getReportFilters();

    const repositoryResult = await NewSaleReportRepository.getReportSellablesResults(
      {
        ...filters,
        order: this.getOrder(),
        page: this.viewModel.pagination.page
      }
    );

    if (repositoryResult.success) {
      this.viewModel.results = repositoryResult.data.sellables;
      this.viewModel.pagination = {
        ...this.viewModel.pagination,
        total: repositoryResult.data.pagination.total_items,
        perPage: repositoryResult.data.pagination.per_page
      };
    } else if (!repositoryResult.userCancelled) {
      this.sendNotification({
        type: "error",
        title: "Oops",
        content:
          "Não foi possível carregar os produtos.\nTente novamente mais tarde ou entre em contato com o suporte."
      });
    }
  }

  async loadSaleResults() {
    this.renewCancelToken();
    const filters = await this.getReportFilters();

    const repositoryResult = await NewSaleReportRepository.getReportTotalSalesTable(
      {
        ...filters,
        order: this.getOrder(),
        page: this.viewModel.pagination.page
      }
    );

    if (repositoryResult.success) {
      this.viewModel.results = repositoryResult.data.sales;

      this.viewModel.pagination = {
        ...this.viewModel.pagination,
        total: repositoryResult.data.pagination.total_items,
        perPage: repositoryResult.data.pagination.per_page
      };
    } else if (!repositoryResult.userCancelled) {
      this.sendNotification({
        type: "error",
        title: "Oops",
        content:
          "Não foi possível carregar as vendas do vendedor selecionado.\nTente novamente mais tarde ou entre em contato com o suporte."
      });
    }
  }

  async loadSummary() {
    const filters = await this.getReportFilters();

    const summaryResult = await NewSaleReportRepository.getReportSellablesResults(filters);

    if (summaryResult.success && summaryResult.data.totals) {
      const { revenues_value, costs_value, profit } = summaryResult.data.totals;

      this.viewModel.summary = {
        revenues: revenues_value,
        costs: costs_value,
        profit
      };
    }
  }

  async getReportFilters() {
    const { product, user, filters } = this.viewModel;
    const { dateFrom, dateTo } = filters;
    const currentUser = await this.mRuntime.get("currentUser");
    const { company_uuid } = currentUser;

    return {
      from: Moment(dateFrom)
        .startOf("day")
        .toISOString(),
      to: Moment(dateTo)
        .endOf("day")
        .toISOString(),
      company_uuid,
      status: "active",
      sellable_uuid: product ? product.uuid : null,
      salesman_uuid: user ? user.uuid : null
    };
  }

  getProductFilters(query) {
    return [
      { connector: "OR", field: "code", operation: "LIKE", value: query },
      { connector: "OR", field: "name", operation: "LIKE", value: query },
      { connector: "AND", field: "deleted", operation: "NOT", value: "true" }
    ];
  }

  async getUserFilters(query) {
    const companyUuid = (await this.mRuntime.get("currentUser")).company_uuid;
    const filters = [];

    if (query) {
      ["name", "username"].forEach(field =>
        filters.push({
          connector: "OR",
          field,
          operation: "LIKE",
          value: query
        })
      );
    }

    filters.push(
      { connector: "AND", field: "deleted", operation: "NOT", value: true },
      {
        connector: "AND",
        field: "company_uuid",
        operation: "EQ",
        value: companyUuid
      }
    );

    return filters;
  }

  async onDownload(format) {
    this.viewModel.loading = true;

    switch (format) {
      case "csv":
        await this._downloadCSV();
        break;

      default:
        this.sendNotification({
          type: "error",
          title: "Oops...",
          content: "Ainda não suportamos esse formato"
        });
    }

    this.viewModel.loading = false;
  }

  formatDocumentType(type) {
    return DocumentTypes[type];
  }

  async _loadProductResultsDownload() {
    this.renewCancelToken();

    const filters = await this.getReportFilters();
    filters.count = 99999;

    const repositoryResult = await NewSaleReportRepository.getReportSellablesResults(
      {
        ...filters,
        order: this.getOrder(),
        page: 1
      }
    );

    if (repositoryResult.success) {
      return repositoryResult.data.sellables;
    }
  }

  async _loadSaleResultsDownload() {
    this.renewCancelToken();
    const filters = await this.getReportFilters();
    filters.count = 99999;

    const repositoryResult = await NewSaleReportRepository.getReportTotalSalesTable(
      {
        ...filters,
        order: this.getOrder(),
        page: 1
      }
    );

    if (repositoryResult.success) {
      return repositoryResult.data.sales;
    }
  }

  async _downloadCSV() {
    const { product, mode } = this.viewModel;
    let content, results;

    if (mode === "sale") {
      results = await this._loadSaleResultsDownload();
      content = [
        "Data; Número do Documento; Status; Faturamento Total; Custos; Descontos; Lucro"
      ];
      results.forEach(item => {
        content.push(
          `${Moment(item.activated_at).format(
            "DD/MM/YYYY HH:mm"
          )}; ${this.formatDocumentType(item.type)} n.: ${item.number}; ${
            SaleStatus[item.status]
          }; ${fn.centsToReal(item.revenues_value)}; ${fn.centsToReal(
            item.costs_value
          )}; ${fn.centsToReal(item.discounts_value)}; ${fn.centsToReal(
            item.profit
          )}`
        );
      });
      const contentBlob = new Blob([content.join("\n")], {
        type: "application/csv;charset=utf-8"
      });
      FileSaver.saveAs(contentBlob, `relatorio-de-vendas-${product.name}.csv`);
    } else {
      results = await this._loadProductResultsDownload();
      content = [
        "Código do Produto; Nome; Quantidade; Faturamento Total; Custos; Descontos; Lucro; Saldo Disponível; Valor do Estoque"
      ];
      results.forEach(item => {
        content.push(
          `${item.product_code}; ${item.product_name}; ${
            item.quantities_value
          }; ${fn.centsToReal(item.revenues_value)}; ${fn.centsToReal(
            item.costs_value
          )}; ${fn.centsToReal(item.discounts_value)}; ${fn.centsToReal(
            item.profit
          )}; ${fn.centsToReal(item.stock_count)} ; ${fn.centsToReal(
            item.stock_value
          )}`
        );
      });
      const contentBlob = new Blob([content.join("\n")], {
        type: "application/csv;charset=utf-8"
      });
      FileSaver.saveAs(contentBlob, `relatorio-de-vendas-por-produto.csv`);
    }
  }

  getOrder() {
    const { order } = this.viewModel;
    return order.direction === "DESC" ? `-${order.field}` : order.field;
  }

  onSelectProduct(selectedProduct) {
    this.viewModel.product = selectedProduct;
    this.viewModel.pagination.page = 1;

    if (selectedProduct.uuid && this.viewModel.mode === "product") {
      this.viewModel.order = { field: "activated_at", direction: "ASC" };
    } else {
      this.viewModel.order = { field: "product_name", direction: "ASC" };
    }

    this.load();
  }

  onSelectUser(selectedUser) {
    this.viewModel.user = selectedUser;
    this.viewModel.pagination.page = 1;
    this.load();
  }

  onFilterChange(filters) {
    if (filters.dateFrom && filters.dateTo) {
      window.location.hash = `?&inicio=${Moment(filters.dateFrom).format(
        "DD-MM-YYYY"
      )}&fim=${Moment(filters.dateTo).format("DD-MM-YYYY")}`;
    }
    for (const key in filters) {
      if (filters[key] !== this.viewModel.filters[key]) {
        this.viewModel.filters = {
          ...this.viewModel.filters,
          ...filters
        };
        this.viewModel.pagination.page = 1;

        this.load();
        break;
      }
    }
  }

  onPageChange(page) {
    this.viewModel.pagination.page = page;
    this.load();
  }

  onOrderChange(field, direction) {
    this.viewModel.order = { field, direction };
    this.load();
  }
}



// WEBPACK FOOTER //
// ./src/components/app/management/product-sales-report/presenter.js